// @ts-nocheck
import { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resendOtp, verifyEmail } from '../../services/registration';
import OtpCodeInput from '../../components/codeField/OtpCodeInput';
import { getAccountId, getAccountInfo, setError } from '../../utils/storage';
import { getApiErrorMessage, handleApiError } from '../../utils/apiError';
import { useResendCountdown } from '../../hooks/useResendCountdown';
import {
    AnalyticsEvent,
    durationSecSince,
    getOtpStartedAt,
    markOtpStarted,
    resolveAnalyticsError,
    trackRegistrationFailed,
    useAnalytics,
} from '../../analytics';
import { isTerminalOtpError, useOtpAnalyticsTracking } from '../../analytics/hooks/useOtpAnalyticsTracking';
import useLogger from '../../hooks/useLogger';
import { EMAIL_VERIFICATION_ACTIONS, LOG_STATUSES } from '../../logging/LoggingConstants';
import RegistrationPage, { PAGE_BODY_TOP_CLASS } from '../../components/layouts/RegistrationPage';
import { cn } from '../../utils/cn';
import PageHeader from '../../components/PageHeader';
import CustomButton from '../../components/customButton/CustomButton';

import emailIcon from '../../assets/svgs/email-icon.svg';
import { showOtpErrorToast } from '../../components/toast/showOtpErrorToast';
import PageLoaderOverlay from '../../components/loading/PageLoaderOverlay';
import Message from '../../components/Message';
import ActionLink from '../../components/actionLink/ActionLink';

export default function EmailVerification() {
    const accountId = getAccountId();
    const accountInfo = getAccountInfo();
    const navigate = useNavigate();
    const { log } = useLogger();
    const { track } = useAnalytics();
    const [pinCode, setPinCode] = useState('');
    const { counter, reset: resetCountdown, stop: stopCountdown } = useResendCountdown(120);
    const [isLoading, setIsLoading] = useState(false);
    const [otpError, setOtpError] = useState(false);
    const { t } = useTranslation();
    const otpWrapperRef = useRef(null);
    const {
        verifyCount,
        resendCount,
        maxVerifyAttempts,
        maxResends,
        beginVerifyAttempt,
        recordVerifyFailure,
        beginResend,
        recordResendSuccess,
        shouldTrackExpired,
        markExpiredTracked,
        expiredAttempt,
    } = useOtpAnalyticsTracking();

    const otpDisabled = counter === 0 || verifyCount >= maxVerifyAttempts;
    const runOutAttempts = !isLoading && counter === 0 && resendCount >= maxResends;

    useEffect(() => {
        const input = otpWrapperRef.current?.querySelector('input');
        input?.focus();
    }, []);

    useEffect(() => {
        if (counter === 0) {
            setPinCode('');
            setOtpError(false);
            if (shouldTrackExpired()) {
                markExpiredTracked();
                track(AnalyticsEvent.OTP_EXPIRED, {
                    otp_type: 'email',
                    attempt: expiredAttempt(),
                });
            }
        }
    }, [counter, track, shouldTrackExpired, markExpiredTracked, expiredAttempt]);

    const handleVerify = (otp = pinCode) => {
        const attempt = beginVerifyAttempt();
        if (attempt == null) {
            return;
        }

        setIsLoading(true);
        const code = parseInt(otp, 10);

        verifyEmail(accountId, code)
            .then(() => {
                log(EMAIL_VERIFICATION_ACTIONS.EMAIL_VERIFICATION, LOG_STATUSES.SUCCESS);
                track(AnalyticsEvent.OTP_VERIFIED, {
                    otp_type: 'email',
                    attempt,
                    duration_sec: durationSecSince(getOtpStartedAt('email')) ?? 0,
                });
                markOtpStarted('phone');
                track(AnalyticsEvent.OTP_SENT, {
                    otp_type: 'phone',
                    attempt: 1,
                    is_resend: false,
                    resend_count: 0,
                });
                navigate('/phone');
            })
            .catch((e) => {
                const message = getApiErrorMessage(e, 'OtpFail');
                const { error_code, error_message } = resolveAnalyticsError({
                    error: e,
                    fallback: 'OtpFail',
                });

                if (isTerminalOtpError(error_code)) {
                    trackRegistrationFailed({
                        step: 'email_otp',
                        error_code,
                        error_message,
                    });
                    navigate(`/error?errorCode=error${error_code}`);
                    return;
                }

                track(AnalyticsEvent.OTP_FAILED, {
                    otp_type: 'email',
                    attempt,
                    duration_sec: durationSecSince(getOtpStartedAt('email')) ?? 0,
                    error_code,
                    error_message,
                });
                // Restart timer so the next verify attempt is timed independently.
                markOtpStarted('email');

                setOtpError(true);
                setError(message);
                showOtpErrorToast(t('errorOtp'));

                const { attempt: failedAttempt, attemptsExceeded } =
                    recordVerifyFailure();
                if (failedAttempt >= maxVerifyAttempts) {
                    stopCountdown();
                }
                if (attemptsExceeded) {
                    track(AnalyticsEvent.OTP_ATTEMPTS_EXCEEDED, {
                        otp_type: 'email',
                        attempt: failedAttempt,
                    });
                }
            })
            .finally(() => setIsLoading(false));
    };

    const handleResend = () => {
        setIsLoading(true);
        const nextResendCount = beginResend();
        setPinCode('');

        resendOtp(accountId, { type: 1 })
            .then(() => {
                log(EMAIL_VERIFICATION_ACTIONS.RESEND_EMAIL_OTP, LOG_STATUSES.SUCCESS);
                markOtpStarted('email');
                recordResendSuccess(nextResendCount);
                track(AnalyticsEvent.OTP_RESENT, {
                    otp_type: 'email',
                    resend_count: nextResendCount,
                });
                resetCountdown();
            })
            .catch((e) => {
                trackRegistrationFailed({
                    step: 'email_otp',
                    error: e,
                    fallback: '3015',
                });
                handleApiError(e, navigate, { fallback: '3015' });
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <>
            <PageLoaderOverlay active={isLoading} />
            <RegistrationPage
                contentClassName="xl:max-w-[30rem] mx-auto w-full"
                stickyFooter
                footer={
                    runOutAttempts ? (
                        <ActionLink to="/" className="md:w-fit mx-auto mt-4">
                            {t('errorButton')}
                        </ActionLink>
                    ) : null
                }
            >
                <PageHeader
                    title={t('emailTitle')}
                    lead={t('emailVerify')}
                    imgSrc={emailIcon}
                    className="min-w-0"
                >
                    <p className="mt-4 truncate rounded-lg bg-primary/5 px-4 py-2 text-sm text-primary max-w-full">
                        {accountInfo?.email}
                    </p>
                </PageHeader>
                <div
                    className={cn(
                        'flex w-full flex-col items-center text-center',
                        PAGE_BODY_TOP_CLASS
                    )}
                >
                    <div className="flex justify-center w-full" ref={otpWrapperRef}>
                        <OtpCodeInput
                            id="pinCode"
                            value={pinCode}
                            disabled={otpDisabled}
                            hasError={otpError && !otpDisabled}
                            className="my-3"
                            autoFocus
                            onChange={(value) => {
                                setOtpError(false);
                                setPinCode(value);

                                if (value.length === 5 && !isLoading) {
                                    handleVerify(value);
                                }
                            }}
                        />
                    </div>

                    <div className="mt-4 flex flex-col items-center gap-2 text-center">
                        {counter === 0 && resendCount < maxResends && (
                            <CustomButton variant="text" onClick={handleResend}>
                                {t('codeResend')}
                            </CustomButton>
                        )}

                        <span
                            aria-live="polite"
                            className={cn(
                                'text-base font-medium',
                                counter ? 'text-primary' : 'text-custom-bg-disabled'
                            )}
                        >
                            {counter} {t('seconds')}
                        </span>
                    </div>
                    {runOutAttempts && <Message message={t('runOutAttempts')} />}
                </div>
            </RegistrationPage>
        </>
    );
}
